/**
 * NAV-1-DENSITY-FIRST-RUN-1 — telemetry_events + aegis_density_telemetry schemas.
 *
 * EXEC-49 telemetry payload schemas. Validates `density.toggle` events and
 * the rolling-window aggregate documents written by aggregateDensityTelemetry.
 *
 * PII discipline (per EXEC-50 ethics framework + dd-decision-006):
 *   - userIdHash: hashed user identifier; raw uid never persisted.
 *   - No persona profiling fields.
 *   - Aggregates only — no per-event narrative.
 *
 * Authority chain:
 *   - dd-decision-006 (DENSITY-OVERRIDE-DELIBERATION-1)
 *   - BRAND-BOOK §XV §5.5 (DENSITY-DOCTRINE-v1.1-AMENDMENT-1)
 *   - EXEC-49 §6.a re-open trigger (40% operational toggle-away rate, 90d window, 10-user floor)
 */

import { z } from 'zod';
import { DensityArchetypeSchema, DensityModeSchema } from './user-preferences.js';

export const SURFACE_CLASS_VALUES = Object.freeze(['operational', 'standard', 'calm']);
export const SurfaceClassSchema   = z.enum(SURFACE_CLASS_VALUES);

/**
 * Single density.toggle event written to telemetry_events on every user
 * toggle. Retention: 90 days (Council 005 §5.4 ephemeral telemetry).
 */
export const DensityToggleEventSchema = z.object({
  eventType:      z.literal('density.toggle'),
  tenantId:       z.string().min(1),
  userIdHash:     z.string().min(1),
  fromArchetype:  DensityArchetypeSchema.nullable(),
  fromMode:       DensityModeSchema.nullable(),
  toArchetype:    DensityArchetypeSchema,
  toMode:         DensityModeSchema,
  surfaceClass:   SurfaceClassSchema,
  // ISO string at emit time. Cloud Function aggregator may also stamp
  // serverTimestamp on write; both are tolerated.
  timestamp:      z.string(),
}).passthrough();

/**
 * Per-user 7-day rolling aggregate. Written by aggregateDensityTelemetry
 * to aegis_density_telemetry/{tenantId}/users/{userIdHash}.
 */
export const DensityUserRollingAggregateSchema = z.object({
  tenantId:        z.string().min(1),
  userIdHash:      z.string().min(1),
  rollingWindow7d: z.object({
    toggleCount:               z.number().int().nonnegative(),
    operationalToggleCount:    z.number().int().nonnegative(),
    standardToggleCount:       z.number().int().nonnegative(),
    calmToggleCount:           z.number().int().nonnegative(),
  }),
  lastUpdatedAt:   z.string(),
}).passthrough();

/**
 * Tenant-level 90-day rolling aggregate. Written to
 * aegis_density_telemetry_aggregates/{tenantId}. Retention: 2 years.
 *
 * triggerCandidate flips true when:
 *   - eligibleUserCount >= 10 AND
 *   - operationalToggleAwayRate > 0.40
 *
 * triggerFirstSurfacedAt is sticky — set once when triggerCandidate first
 * flips true, preserved across subsequent runs for §6.a Council 005-v2
 * deliberation chip filing (idempotent re-open trigger).
 */
export const DensityTenantAggregateSchema = z.object({
  tenantId:         z.string().min(1),
  rollingWindow90d: z.object({
    eligibleUserCount:           z.number().int().nonnegative(),
    operationalToggleAwayRate:   z.number().min(0).max(1),
    triggerCandidate:            z.boolean(),
    triggerFirstSurfacedAt:      z.string().nullable(),
  }),
  lastUpdatedAt:    z.string(),
}).passthrough();

export default {
  DensityToggleEventSchema,
  DensityUserRollingAggregateSchema,
  DensityTenantAggregateSchema,
  SurfaceClassSchema,
};
